
import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { lumi } from './lib/lumi';
import { Timeline } from './components/ui/timeline';
import InteractiveSelector from './components/ui/interactive-selector';
import { HoverSlider, HoverSliderImage, HoverSliderImageWrap, TextStaggerHover } from './components/ui/animated-slideshow';
import { LocationModal } from './components/ui/location-modal';
import { SocialCardModal } from './components/ui/social-card-modal';
import InteractiveSection from './components/ui/interactive-section';
import Settings from './pages/Settings';

interface Project {
  _id: string;
  name: string;
  description: string;
  note?: string;
  logoUrl?: string;
  websiteUrl: string;
  resourceUrl?: string;
  status: string;
  displayOrder: number;
}

interface Location {
  _id: string;
  name: string;
  year: number;
  imageUrl: string;
  description?: string;
}

interface SocialCard {
  id: string;
  title: string;
  imageUrl: string;
  platform: string;
  description: string;
  linkUrl: string;
}

function HomePage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [previousProjects, setPreviousProjects] = useState<Project[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [modalLocation, setModalLocation] = useState<Location | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalSocialCard, setModalSocialCard] = useState<SocialCard | null>(null);
  const [isSocialModalOpen, setIsSocialModalOpen] = useState(false);
  const [lastUpdateDate, setLastUpdateDate] = useState('2025.8.1');

  // 隐藏入口相关状态
  const [clickCount, setClickCount] = useState(0);
  const [lastClickTime, setLastClickTime] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 获取项目数据
        const { list: projectList } = await lumi.entities.projects.list({
          sort: { displayOrder: 1 },
          filter: { status: { $in: ['active', 'completed'] } }
        });
        setProjects(projectList as unknown as Project[]);

        // 获取过往项目数据
        const { list: previousProjectList } = await lumi.entities.projects.list({
          sort: { displayOrder: 1 },
          filter: { status: 'inactive' }
        });
        setPreviousProjects(previousProjectList as unknown as Project[]);

        // 获取地点数据
        const { list: locationList } = await lumi.entities.locations.list({
          sort: { year: -1, createdAt: -1 }
        });
        setLocations(locationList as unknown as Location[]);

        // 提取所有唯一年份并按降序排列
        const years = Array.from(
          new Set((locationList as unknown as Location[]).map((loc) => loc.year))
        ).sort((a, b) => b - a);

        setAvailableYears(years);

        // 默认选中最新（最大）的年份
        if (years.length > 0) {
          setActiveTab(years[0]);
        }

        // 获取站点配置
        const { list: configList } = await lumi.entities.siteConfig.list({
          filter: { configKey: 'lastUpdateDate' }
        });
        if (configList && configList.length > 0) {
          setLastUpdateDate(configList[0].configValue);
        }
      } catch (error) {
        console.error('获取数据失败:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // 处理隐藏入口点击
  const handleHiddenEntrance = () => {
    const now = Date.now();
    const timeDiff = now - lastClickTime;

    // 如果距离上次点击超过2秒，重置计数
    if (timeDiff > 2000) {
      setClickCount(1);
    } else {
      setClickCount((prev) => prev + 1);
    }

    setLastClickTime(now);

    // 如果连续快速点击达到10次，导航到设置页面
    if (clickCount >= 9) {// 因为是 prev + 1，所以这里用 9
      console.log('🎯 隐藏入口触发！导航到设置页面...');
      setClickCount(0); // 重置计数
      navigate('/settings');
    }

    // 调试信息
    console.log(`🔢 点击计数: ${clickCount + 1}/10, 时间间隔: ${timeDiff}ms`);
  };

  // 滚动到页面顶部
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // 打开地点弹窗
  const handleLocationClick = (location: Location) => {
    setModalLocation(location);
    setIsModalOpen(true);
  };

  // 关闭地点弹窗
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalLocation(null);
  };

  // 打开社交名片弹窗
  const handleSocialCardClick = (socialCard: SocialCard) => {
    setModalSocialCard(socialCard);
    setIsSocialModalOpen(true);
  };

  // 关闭社交名片弹窗
  const handleCloseSocialModal = () => {
    setIsSocialModalOpen(false);
    setModalSocialCard(null);
  };

  // Timeline数据
  const timelineData = [
  {
    title: "2024-NOW",
    content:
    <div>
          <h4 className="text-3xl font-bold mb-4 text-black">互联网 C 端产品经理</h4>
          
          <div className="bg-black text-white p-8 mb-6 border-4 border-black">
            <h5 className="text-2xl font-bold mb-4 border-b-2 border-white pb-2">BlockDance</h5>
            <div className="space-y-4 text-lg">
              <div>Work Life Balance</div>
            </div>
          </div>
        </div>

  },
  {
    title: "2023-2024",
    content:
    <div>
          <h4 className="text-3xl font-bold mb-4 text-black">校招 - 互联网 B 端产品经理</h4>
          
          <div className="bg-white border-4 border-black p-8">
            <h5 className="text-2xl font-bold mb-4 border-b-2 border-black pb-2">字节跳动 - 飞书</h5>
            <div className="space-y-4 text-lg">
              <div>
                <span className="font-bold">关键词：</span>
                CRM 中台产品 低代码 AI
              </div>
              <div>周均工作时间 60-70 个小时、月均一半出差、人均挂病假工作</div>
              <div>经历了三任 +1 和 裁员潮</div>
              <div>干了一年，没被裁但是身体垮了，裸辞</div>
            </div>
          </div>
        </div>

  },
  {
    title: "2020-2022",
    content:
    <div>
          <h4 className="text-3xl font-bold mb-4 text-black">实习 - 咨询</h4>
          
          <div className="bg-white border-4 border-black p-8">
            <h5 className="text-2xl font-bold mb-4 border-b-2 border-black pb-2">在毕马威、益普索和飞书都干过咨询</h5>
            <div className="space-y-4 text-lg text-black">
              <div>在字节稀里糊涂转行产品</div>
              <div>再到这里B端转C端，业务大换</div>
            </div>
          </div>
        </div>

  }];


  // Gallery数据 - 适配HoverSlider，增加完整社交名片信息
  const gallerySlides: SocialCard[] = [
  {
    id: "slide-1",
    title: "@Ehow",
    imageUrl: "https://res.cloudinary.com/dmchtcpam/image/upload/v1753965548/img_v3_02gk_fb41da86-a527-4f1d-9162-b7f430f7d37g_1_vjc7hl.jpg",
    platform: "小红书",
    description: "蹭热点蹭出 10 万总阅读",
    linkUrl: "https://www.xiaohongshu.com/explore/6278c317000000002103ac1c?xsec_token=ABoIK-3PfiZ84jIIycwt-YjMBod6lGlmumFmsKIzKMhi8=&xsec_source=pc_user"
  },
  {
    id: "slide-2",
    title: "@Ehooow",
    imageUrl: "https://res.cloudinary.com/dmchtcpam/image/upload/v1753965548/IMG_9159_1_bju4xh.jpg",
    platform: "Bilibili",
    description: " 年更｜杂交区",
    linkUrl: "https://www.bilibili.com/video/BV1YByWY3EtE/?spm_id_from=333.999.list.card_archive.click&vd_source=8ddbc2de66bdd5b7335d0cab8b9d9528"
  },
  {
    id: "slide-3",
    title: "@零家壹",
    imageUrl: "https://res.cloudinary.com/dmchtcpam/image/upload/v1753965548/img_v3_02gk_03695046-6a16-49b6-8997-c188f009a90g_1_crsbn8.png",
    platform: "公众号",
    description: "累计阅读超 50 万｜由于结束异地而断更",
    linkUrl: "https://mp.weixin.qq.com/s/NLhEInIUNn2z7vzB5ysC6g"
  }];


  // 人生读物数据 - 从InteractiveSelector中提取的内容
  const readingList = [
  {
    id: 1,
    title: "爱乐之城",
    subtitle: "Lalaland",
    imageUrl: "https://m.media-amazon.com/images/M/MV5BZDI0Y2FiMzgtMzkxNC00ODdmLTk0NWEtZDMwNjdmZGE2M2ZkXkEyXkFqcGc@._V1_.jpg"
  },
  {
    id: 2,
    title: "三傻大闹宝莱坞",
    subtitle: "3 Idiots",
    imageUrl: "https://m.media-amazon.com/images/M/MV5BMTU1Nzg0NzczMF5BMl5BanBnXkFtZTcwMzk0NDMwNQ@@._V1_.jpg"
  },
  {
    id: 3,
    title: "星际穿越",
    subtitle: "Interstellar",
    imageUrl: "https://m.media-amazon.com/images/M/MV5BMjA3NTEwOTMxMV5BMl5BanBnXkFtZTgwMjMyODgxMzE@._V1_.jpg"
  },
  {
    id: 4,
    title: "葬送的芙莉莲",
    subtitle: "葬送のフリーレン",
    imageUrl: "https://m.media-amazon.com/images/M/MV5BZTUzNmVkOTctZDk4MC00N2I4LTk1NTktMzE1YjE2MzFkZDRlXkEyXkFqcGc@._V1_FMjpg_UX1920_.jpg"
  },
  {
    id: 5,
    title: "强风吹拂",
    subtitle: "風が強く吹いている",
    imageUrl: "https://i0.wp.com/anitrendz.net/news/wp-content/uploads/2019/04/Screen-Shot-2019-04-28-at-12.59.01-PM.png?w=1392&ssl=1"
  },
  {
    id: 6,
    title: "三体",
    subtitle: "The Three-Body Problem",
    imageUrl: "https://m.media-amazon.com/images/I/91pU4vwOJUL._SL1500_.jpg"
  }];


  // 根据年份过滤地点数据
  const filteredLocations = locations.filter((location) => location.year === activeTab);

  return (
    <div className="bg-white text-black font-mono">
      {/* Navigation - 移动端适配 */}
      <nav className="fixed top-0 left-0 w-full bg-white border-b-0 border-black z-50 px-4 py-3 md:p-4">
        <div className="flex justify-between items-center max-w-none">
          <div
            className="text-lg md:text-2xl font-bold cursor-pointer hover:bg-black hover:text-white px-2 py-1 transition-none"
            onClick={scrollToTop}>

            <span className="hidden sm:inline">Ehow's User Manual</span>
            <span className="sm:hidden">Ehow's Manual</span>
          </div>
          <div className="flex space-x-4 md:space-x-8">
            <a href="#about" className="text-sm md:text-lg hover:bg-black hover:text-white px-2 py-1 transition-none">关于</a>
            <a href="#work" className="text-sm md:text-lg hover:bg-black hover:text-white px-2 py-1 transition-none">工作</a>
            <a href="#life" className="text-sm md:text-lg hover:bg-black hover:text-white px-2 py-1 transition-none">生活</a>
            <a href="#footer" className="text-sm md:text-lg hover:bg-black hover:text-white px-2 py-1 transition-none">联系</a>
          </div>
        </div>
      </nav>

      {/* Hero Section - 移动端适配 */}
      <section id="hero" className="min-h-screen flex items-center justify-start pt-20 md:pt-20">
        <div className="w-full max-w-none px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-0">
            {/* 主标题和内容 - 移动端优先显示 */}
            <div className="md:col-span-8 order-2 md:order-1">
              <motion.h1
                className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-black leading-none mb-6 md:mb-8 text-center md:text-left"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}>

                逸豪的<br />
                个人说明书
              </motion.h1>
              <div className="text-lg md:text-2xl mb-6 md:mb-8 max-w-full md:max-w-lg">
                👋 Hi，这是我的个人说明书，你可以在这里快速认识我和我的工作。
              </div>
              <div className="hidden md:flex flex-col sm:flex-row gap-3 sm:gap-4">
                <a href="#about" className="bg-white text-black px-6 md:px-8 py-3 md:py-4 text-base md:text-lg font-bold border-2 border-black hover:bg-black hover:text-white transition-none text-center">
                  关于
                </a>
                <a href="#work" className="bg-white text-black px-6 md:px-8 py-3 md:py-4 text-base md:text-lg font-bold border-2 border-black hover:bg-black hover:text-white transition-none text-center">
                  工作
                </a>
                <a href="#life" className="bg-white text-black px-6 md:px-8 py-3 md:py-4 text-base md:text-lg font-bold border-2 border-black hover:bg-black hover:text-white transition-none text-center">
                  生活
                </a>
              </div>
            </div>
            
            {/* 头像图片 - 移动端在上方显示 */}
            <div className="md:col-span-4 flex justify-center md:justify-end order-1 md:order-2">
              <div className="w-64 h-80 md:w-80 md:h-96 border-4 border-black overflow-hidden">
                <img
                  src="https://res.cloudinary.com/dmchtcpam/image/upload/v1753960196/IMG_4009_cq0e3d.jpg"
                  alt="逸豪的照片"
                  className="w-full h-full object-cover" />

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section - 移动端适配 */}
      <section id="about" className="min-h-screen py-20 bg-black text-white">
        <div className="px-4 md:px-8">
          {/* 移动端：标题在顶部横向显示，桌面端：保持侧边布局 */}
          <div className="block md:hidden mb-16">
            <h2 className="text-4xl sm:text-6xl font-black text-center">
              ABOUT
            </h2>
          </div>
          
          <div className="hidden md:grid grid-cols-12 gap-0">
            <div className="col-span-3">
              <h2 className="text-6xl font-black mb-8 transform -rotate-90 origin-left mt-32">
                ABOUT
              </h2>
            </div>
            <div className="col-span-9 pl-8">
              <div className="grid grid-cols-2 gap-16">
                <div>
                  <h3 className="text-3xl font-bold mb-6 border-b-2 border-white pb-2">基本信息</h3>
                  <div className="space-y-4 text-lg leading-relaxed">
                    <p>
                      你好！我叫黄逸豪，在 2024.10.24 加入 BlockDance 担任产品经理。
                    </p>
                    <p>
                      在此之前，我在字节飞书做了一年的 CRM 相关的内部工具产品。
                    </p>
                    <p>我的位置屏幕上有很高的富贵竹，屏幕两侧有两个小舞狮（佛山人），欢迎随时过来和我沟通～

                    </p>
                  </div>
                </div>
                <div>
                  <div className="space-y-8">
                    <div>
                      <h3 className="text-3xl font-bold mb-6 border-b-2 border-white pb-2">学校和专业</h3>
                      <div className="text-lg">
                        <div className="mb-2">本科/硕士：中山大学 - 岭南学院 - 国际商务</div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-3xl font-bold mb-6 border-b-2 border-white pb-2">个人特点</h3>
                      <div className="space-y-2 text-lg">
                        <div>MBTI：INFJ｜利他主义｜理想主义｜计划性｜创新</div>
                        <div>格局大，ego 小</div>
                        <div>飞书深度使用者，尤其是多维表格｜Codex</div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-3xl font-bold mb-6 border-b-2 border-white pb-2">Contact Me</h3>
                      <div className="space-y-2 text-lg">
                        <div>电话：17688230414</div>
                        <div>微信：huangehow</div>
                        <div>邮箱：</div>
                        <div className="ml-4 space-y-1">
                          <div>1. 工作/谷歌 - huangehow@yeah.net</div>
                          <div>2. 个人 - huangyh97@mail2.sysu.edu.cn</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 移动端：单列布局 */}
          <div className="block md:hidden">
            <div className="space-y-12">
              {/* 基本信息 */}
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-6 border-b-2 border-white pb-2">基本信息</h3>
                <div className="space-y-4 text-base sm:text-lg leading-relaxed">
                  <p>
                    你好！我叫黄逸豪，在 2024.10.24 加入 BlockDance 担任产品经理。
                  </p>
                  <p>
                    在此之前，我在字节飞书做了一年的 CRM 相关的内部工具产品。
                  </p>
                  <p>
                    我的位置屏幕上有很高的富贵竹，以及通常会用咖啡杯养着一枝花，欢迎随时过来和我沟通～
                  </p>
                </div>
              </div>

              {/* 学校和专业 */}
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-6 border-b-2 border-white pb-2">学校和专业</h3>
                <div className="text-base sm:text-lg">
                  <div className="mb-2">本科/硕士：中山大学 - 岭南学院 - 国际商务</div>
                </div>
              </div>
              
              {/* 个人特点 */}
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-6 border-b-2 border-white pb-2">个人特点</h3>
                <div className="space-y-2 text-base sm:text-lg">
                  <div>MBTI：INFJ｜利他主义｜理想主义｜计划性｜创新</div>
                  <div>格局大，ego 小</div>
                  <div>飞书深度使用者，尤其是多维表格</div>
                </div>
              </div>
              
              {/* Contact Me */}
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-6 border-b-2 border-white pb-2">Contact Me</h3>
                <div className="space-y-2 text-base sm:text-lg">
                  <div>电话：17688230414</div>
                  <div>微信：huangehow</div>
                  <div>邮箱：</div>
                  <div className="ml-4 space-y-1 text-sm sm:text-base">
                    <div>1. 工作/谷歌 - huangehow@yeah.net</div>
                    <div>2. 个人 - huangyh97@mail2.sysu.edu.cn</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Work Section */}
      <section id="work" className="min-h-screen py-20 bg-white text-black">
        <div className="px-8">
          <h2 className="text-8xl font-black mb-16 text-center">MY JOB</h2>
          
          {/* 新增描述文案 */}
          <div className="text-center mb-32">
            <div className="max-w-4xl mx-auto text-2xl leading-relaxed">
              目前主要负责产品组管理和部分项目推进。
            </div>
          </div>
          
          {/* 项目展示区域 */}
          <div className="mb-32">
            <h3 className="text-6xl font-black mb-16 text-left">PROJECTS WIP</h3>
            
            {loading ?
            <div className="text-center text-2xl">加载项目数据中...</div> :

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                {projects.map((project) =>
              <div key={project._id} className="border-4 border-black p-8">
                    {/* 项目Logo */}
                    <div className="flex justify-center mb-8">
                      <div className="w-32 h-32 border-0 border-black bg-white overflow-hidden">
                        {project.logoUrl ?
                    <img
                      src={project.logoUrl}
                      alt={`${project.name} logo`}
                      className="w-full h-full object-cover" /> :


                    <div className="w-full h-full bg-black flex items-center justify-center">
                            <span className="text-white text-xl font-bold">
                              {project.name.charAt(0)}
                            </span>
                          </div>
                    }
                      </div>
                    </div>
                    
                    {/* 项目信息 */}
                    <div className="text-center">
                      {/* 项目名称 */}
                      <div className="mb-6">
                        <h4 className="text-3xl font-black">{project.name}</h4>
                      </div>
                      
                      <div className="text-lg mb-8 leading-relaxed">
                        {project.description}
                      </div>
                      
                      {/* 链接按钮 */}
                      <div className="flex flex-col space-y-3">
                        <a
                      href={project.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-black text-white px-6 py-3 text-lg font-bold hover:bg-white hover:text-black border-2 border-black transition-none">

                          访问官网
                        </a>
                        {project.resourceUrl &&
                    <a
                      href={project.resourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white text-black px-6 py-3 text-lg font-bold hover:bg-black hover:text-white border-2 border-black transition-none">

                            项目资料
                          </a>
                    }
                      </div>
                    </div>
                  </div>
              )}
              </div>
            }
          </div>

          {/* 过往项目展示区域 - 每行3列 */}
          {!loading && previousProjects.length > 0 &&
          <div className="mb-32">
              <h3 className="text-6xl font-black mb-16 text-left">PREVIOUS PROJECTS</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-16">
                {previousProjects.map((project) =>
              <div key={project._id} className="border-4 border-black p-8">
                    {/* 项目Logo */}
                    <div className="flex justify-center mb-8">
                      <div className="w-32 h-32 border-0 border-black bg-white overflow-hidden">
                        {project.logoUrl ?
                    <img
                      src={project.logoUrl}
                      alt={`${project.name} logo`}
                      className="w-full h-full object-cover" /> :


                    <div className="w-full h-full bg-black flex items-center justify-center">
                            <span className="text-white text-xl font-bold">
                              {project.name.charAt(0)}
                            </span>
                          </div>
                    }
                      </div>
                    </div>

                    {/* 项目信息 */}
                    <div className="text-center">
                      {/* 项目名称 */}
                      <div className="mb-6">
                        <h4 className="text-3xl font-black">{project.name}</h4>
                      </div>

                      <div className="text-lg mb-4 leading-relaxed">
                        {project.description}
                      </div>

                      {/* 项目备注 */}
                      {project.note &&
                    <div className="text-base mb-8 leading-relaxed text-gray-600 border-t-2 border-black pt-4">
                          {project.note}
                        </div>
                    }

                      {/* 链接按钮 */}
                      <div className="flex flex-col space-y-3">
                        <a
                      href={project.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-black text-white px-6 py-3 text-lg font-bold hover:bg-white hover:text-black border-2 border-black transition-none">

                          访问官网
                        </a>
                        {project.resourceUrl &&
                    <a
                      href={project.resourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white text-black px-6 py-3 text-lg font-bold hover:bg-black hover:text-white border-2 border-black transition-none">

                            项目资料
                          </a>
                    }
                      </div>
                    </div>
                  </div>
              )}
              </div>
            </div>
          }

          {/* Work with me 板块 - 移动端适配 */}
          <div className="mb-32">
            <h3 className="text-6xl font-black mb-16 text-left">
              <span className="hidden md:inline">WORKS WITH ME</span>
              <span className="md:hidden">WORKS WITH ME</span>
            </h3>
            
            {/* 桌面端：保持原有布局 */}
            <div className="hidden md:grid grid-cols-12 gap-0">
              <div className="col-span-1 border-r-2 border-black"></div>
              <div className="col-span-11 pl-12">
                <div className="grid grid-cols-2 gap-16">
                  {/* 工作时间 */}
                  <div>
                    <h4 className="text-3xl font-bold mb-8 border-b-4 border-black pb-2">工作时间</h4>
                    <div className="space-y-6 text-lg leading-relaxed">
                      <div className="border-l-4 border-black pl-4">
                        通常在工作日 9:30 - 20:30；其他时间可随时在飞书上联系
                      </div>
                      <div className="border-l-4 border-black pl-4">
                        日常会议安排较频繁；高优事项欢迎随时拉日程；其余事项如方便希望能够提前一天约会议
                      </div>
                      <div className="border-l-4 border-black pl-4">
                        你可以通过我是否有挂起飞书状态判断我的忙碌情况，如有急事请随时加急或者到位置上喊我
                      </div>
                    </div>
                  </div>
                  
                  {/* 工作方式 */}
                  <div>
                    <h4 className="text-3xl font-bold mb-8 border-b-4 border-black pb-2">工作方式</h4>
                    <div className="space-y-6 text-lg leading-relaxed">
                      <div>
                        <div className="font-bold mb-2 border-l-4 border-black pl-4">拒绝口头 PRD</div>
                        <div className="text-base ml-6 mb-2">
                          我会尽量用 PRD 和文档的方式推动需求，目的是能够更好地与各协作方合作，减少理解偏差和从各个地方搜罗信息的时间成本
                        </div>
                        <div className="text-base ml-6 bg-black text-white p-2">
                          → 如果你对文档有不确定，或者发现文档与你的理解/最新变更有偏差，请随时、及时联系我进行更新
                        </div>
                      </div>
                      
                      <div>
                        <div className="font-bold mb-2 border-l-4 border-black pl-4">记 Todos</div>
                        <div className="text-base ml-6 mb-2">
                          个人工作中会将待办事项落到 todo 上进行跟踪，确保事事有着落
                        </div>
                        <div className="text-base ml-6 bg-black text-white p-2">
                          → 如果你需要我的配合，非常欢迎直接通过飞书任务给我记 todo，我会及时联系你推进事项
                        </div>
                      </div>
                      
                      <div>
                        <div className="font-bold mb-2 border-l-4 border-black pl-4">异步沟通</div>
                        <div className="text-base ml-6 mb-2">
                          在每次正式交流之前，我倾向于通过文档与评论的方式先了解双方的关注点与问题，然后再在会议或者交流中聚焦解决
                        </div>
                        <div className="text-base ml-6 bg-black text-white p-2">
                          → 通常我会在评审或正式会议之前一段时间发出文档，我会很高兴如果你能先提前大致阅读内容并给我留评论
                        </div>
                      </div>
                      
                      <div>
                        <div className="font-bold mb-2 border-l-4 border-black pl-4">保持输入</div>
                        <div className="text-base ml-6 mb-2">
                          除了学习做产品功能本身外，还在努力输入AI、数据埋点、SEO、运营推广、商业化等各方面的知识，目标是成为能为公司持续赚钱的产品经理
                        </div>
                        <div className="text-base ml-6 bg-black text-white p-2">
                          → 如果你有任何方面的想法，或者推荐学习的资料，非常高兴能与你交流～
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 移动端：简化布局 */}
            <div className="block md:hidden">
              <div className="space-y-12">
                {/* 工作时间 */}
                <div>
                  <h4 className="text-2xl sm:text-3xl font-bold mb-6 border-b-4 border-black pb-2">工作时间</h4>
                  <div className="space-y-4 text-base sm:text-lg leading-relaxed">
                    <div className="border-l-4 border-black pl-4">
                      通常在工作日 9:30 - 20:30；其他时间可随时在飞书上联系
                    </div>
                    <div className="border-l-4 border-black pl-4">
                      日常会议安排较频繁；高优事项欢迎随时拉日程；其余事项如方便希望能够提前一天约会议
                    </div>
                    <div className="border-l-4 border-black pl-4">
                      你可以通过我是否有挂起飞书状态判断我的忙碌情况，如有急事请随时加急或者到位置上喊我
                    </div>
                  </div>
                </div>
                
                {/* 工作方式 */}
                <div>
                  <h4 className="text-2xl sm:text-3xl font-bold mb-6 border-b-4 border-black pb-2">工作方式</h4>
                  <div className="space-y-6 text-base sm:text-lg leading-relaxed">
                    <div>
                      <div className="font-bold mb-2 border-l-4 border-black pl-4">拒绝口头 PRD</div>
                      <div className="text-sm sm:text-base ml-6 mb-2">
                        我会尽量用 PRD 和文档的方式推动需求，目的是能够更好地与各协作方合作，减少理解偏差和从各个地方搜罗信息的时间成本
                      </div>
                      <div className="text-sm sm:text-base ml-6 bg-black text-white p-2">
                        → 如果你对文档有不确定，或者发现文档与你的理解/最新变更有偏差，请随时、及时联系我进行更新
                      </div>
                    </div>
                    
                    <div>
                      <div className="font-bold mb-2 border-l-4 border-black pl-4">记 Todos</div>
                      <div className="text-sm sm:text-base ml-6 mb-2">
                        个人工作中会将待办事项落到 todo 上进行跟踪，确保事事有着落
                      </div>
                      <div className="text-sm sm:text-base ml-6 bg-black text-white p-2">
                        → 如果你需要我的配合，非常欢迎直接通过飞书任务给我记 todo，我会及时联系你推进事项
                      </div>
                    </div>
                    
                    <div>
                      <div className="font-bold mb-2 border-l-4 border-black pl-4">异步沟通</div>
                      <div className="text-sm sm:text-base ml-6 mb-2">
                        在每次正式交流之前，我倾向于通过文档与评论的方式先了解双方的关注点与问题，然后再在会议或者交流中聚焦解决
                      </div>
                      <div className="text-sm sm:text-base ml-6 bg-black text-white p-2">
                        → 通常我会在评审或正式会议之前一段时间发出文档，我会很高兴如果你能先提前大致阅读内容并给我留评论
                      </div>
                    </div>
                    
                    <div>
                      <div className="font-bold mb-2 border-l-4 border-black pl-4">保持输入</div>
                      <div className="text-sm sm:text-base ml-6 mb-2">
                        除了学习做产品功能本身外，还在努力输入AI、数据埋点、SEO、运营推广、商业化等各方面的知识，目标是成为能为公司持续赚钱的产品经理
                      </div>
                      <div className="text-sm sm:text-base ml-6 bg-black text-white p-2">
                        → 如果你有任何方面的想法，或者推荐学习的资料，非常高兴能与你交流～
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 工作经历时间轴 - 桌面端和移动端分离 */}
          <div className="mb-16">
            <h3 className="text-6xl font-black mb-16 text-left">
              <span className="hidden md:inline">EXPERIENCES</span>
              <span className="md:hidden">EXP.</span>
            </h3>
            
            {/* 桌面端：保持使用Timeline组件 */}
            <div className="hidden md:block w-full">
              <Timeline data={timelineData} />
            </div>

            {/* 移动端：极简扁平化布局 */}
            <div className="block md:hidden">
              <div className="space-y-6">
                {/* 2024-NOW */}
                <div className="border-b-2 border-black pb-6">
                  <div className="mb-4">
                    <h4 className="text-xl font-black mb-2">2024-NOW</h4>
                    <h5 className="text-lg font-bold text-gray-700">互联网 C 端产品经理</h5>
                  </div>
                  <div className="pl-4 border-l-2 border-black">
                    <div className="font-bold mb-2">BlockDance</div>
                    <div className="text-sm text-gray-600">Work Life Balance</div>
                  </div>
                </div>
                
                {/* 2023-2024 */}
                <div className="border-b-2 border-black pb-6">
                  <div className="mb-4">
                    <h4 className="text-xl font-black mb-2">2023-2024</h4>
                    <h5 className="text-lg font-bold text-gray-700">校招 - 互联网 B 端产品经理</h5>
                  </div>
                  <div className="pl-4 border-l-2 border-black">
                    <div className="font-bold mb-2">字节跳动 - 飞书</div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>关键词：CRM 中台产品 低代码 AI</div>
                      <div>周均工作时间 60-70 个小时、月均一半出差、人均挂病假工作</div>
                      <div>经历了三任 +1 和 裁员潮</div>
                      <div>干了一年，没被裁但是身体垮了，裸辞</div>
                    </div>
                  </div>
                </div>
                
                {/* 2020-2022 */}
                <div>
                  <div className="mb-4">
                    <h4 className="text-xl font-black mb-2">2020-2022</h4>
                    <h5 className="text-lg font-bold text-gray-700">实习 - 咨询</h5>
                  </div>
                  <div className="pl-4 border-l-2 border-black">
                    <div className="font-bold mb-2">在毕马威、益普索和飞书都干过咨询</div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>在字节稀里糊涂转行产品</div>
                      <div>再到这里B端转C端，业务大换</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Life Section */}
      <section id="life" className="min-h-screen py-20 bg-black text-white">
        <div className="px-8">
          <h2 className="text-8xl font-black mb-16 text-center">MY LIFE</h2>
          
          {/* 新增生活描述文案 */}
          <div className="text-center mb-32">
            <div className="max-w-4xl mx-auto text-2xl leading-relaxed">
              曾经生活的所有重心都放在了工作上，结果是容易受不可控因素的影响。
              <br />
              现在会努力发展工作以外的重心，重新获得自己生活的掌控感。
              <br />
              最近新的爱好是打羽毛球、旅游和桨板！
            </div>
          </div>

          {/* 人生读物区块 - 桌面端和移动端分离 */}
          <div className="mb-32">
            <h3 className="text-6xl font-black mb-16 text-left">人生读物</h3>
            
            {/* 桌面端：保持InteractiveSelector */}
            <div className="hidden md:block">
              <InteractiveSelector />
            </div>

            {/* 移动端：极简列表展示，移除 description */}
            <div className="block md:hidden">
              <div className="space-y-6">
                {readingList.map((item) =>
                <div key={item.id} className="border-b-2 border-white pb-6">
                    <div className="flex space-x-4">
                      {/* 封面图片 */}
                      <div className="w-16 h-20 border-2 border-white overflow-hidden flex-shrink-0">
                        <img
                        src={item.imageUrl}
                        alt={item.title}
                        className="w-full h-full object-cover" />

                      </div>
                      
                      {/* 内容信息 */}
                      <div className="flex-1">
                        <h4 className="text-lg font-bold mb-1">{item.title}</h4>
                        <div className="text-sm text-gray-300">{item.subtitle}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* 过气博主区块 */}
          <div className="mb-32">
            <h3 className="text-6xl font-black mb-16 text-left">过气博主</h3>
            
            {/* Animated Slideshow */}
            <div className="mt-24">
              <HoverSlider className="min-h-[400px] place-content-center bg-black text-white">
                <div className="flex flex-wrap items-center justify-between gap-8 md:gap-16">
                  <div className="flex flex-col space-y-4 md:space-y-6">
                    {gallerySlides.map((slide, index) =>
                    <TextStaggerHover
                      key={slide.platform}
                      index={index}
                      className="cursor-pointer text-4xl md:text-6xl font-black uppercase tracking-tighter border-l-4 border-white pl-4"
                      text={slide.platform}
                      onClick={() => handleSocialCardClick(slide)} />

                    )}
                  </div>
                  <HoverSliderImageWrap className="![grid-template-columns:100%] ![grid-template-rows:100%] w-80 h-80 md:!w-[32rem] md:!h-96 border-4 border-white">
                    {gallerySlides.map((slide, index) =>
                    <div key={slide.id} onClick={() => handleSocialCardClick(slide)} className="cursor-pointer">
                        <HoverSliderImage
                        index={index}
                        imageUrl={slide.imageUrl}
                        src={slide.imageUrl}
                        alt={slide.title}
                        className="w-full h-full object-cover"
                        loading="eager"
                        decoding="async" />

                      </div>
                    )}
                  </HoverSliderImageWrap>
                </div>
              </HoverSlider>
            </div>
          </div>

          {/* 到处逛逛区块 */}
          <div className="mb-32">
            <h3 className="text-6xl font-black mb-16 text-left">到处逛逛</h3>
            
            {/* Tab 切换 */}
            <div className="mb-12">
              <div className="flex space-x-4 flex-wrap gap-y-4">
                {availableYears.map((year) =>
                <button
                  key={year}
                  onClick={() => setActiveTab(year)}
                  className={`px-8 py-4 text-2xl font-bold border-2 border-white transition-none ${
                  activeTab === year ?
                  'bg-white text-black' :
                  'bg-black text-white hover:bg-white hover:text-black'}`
                  }>

                    {year}
                  </button>
                )}
              </div>
            </div>

            {/* 地点卡片展示 */}
            {loading ?
            <div className="text-center text-2xl">加载地点数据中...</div> :

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredLocations.map((location) =>
              <div
                key={location._id}
                className="bg-white text-black border-4 border-white overflow-hidden cursor-pointer hover:scale-105 transition-transform duration-200"
                onClick={() => handleLocationClick(location)}>

                    {/* 地点图片 */}
                    <div className="w-full h-64 overflow-hidden">
                      <img
                    src={location.imageUrl}
                    alt={location.name}
                    className="w-full h-full object-cover" />

                    </div>
                    
                    {/* 地点名称 */}
                    <div className="p-6">
                      <h4 className="text-2xl font-bold">{location.name}</h4>
                    </div>
                  </div>
              )}
              </div>
            }

            {/* 无数据提示 */}
            {!loading && filteredLocations.length === 0 &&
            <div className="text-center text-2xl text-gray-400">
                {activeTab} 年还没有记录的地方，敬请期待...
              </div>
            }
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="footer" className="bg-white text-black min-h-screen py-12 pt-24 border-t-4 border-black flex flex-col justify-between">
        {/* 桌面端：左右分栏布局，移动端：垂直布局 */}
        <div className="px-4 md:px-8">
          <div className="hidden md:grid grid-cols-12 gap-16">
            {/* 左侧：引用格式的说明文本 */}
            <div className="col-span-7">
              <blockquote className="text-gray-600 text-lg leading-relaxed">
                <div className="font-bold mb-6 text-2xl text-black">写在最后：为什么要写这份个人说明书？</div>
                <div className="space-y-6">
                  <p>
                    在前司，常常看到各种各样有趣的个人说明书，但是自己一直没有写。一方面是 i 人，另一方面也怕自己不够有趣写不出什么内容。直到离职的时候，写了一份离职说明书，除了交接内容外，也简单写了一下个人的一些信息和爱好，却没想到也收到了大家热烈的回应。
                  </p>
                  <p>
                    以前打开一个陌生人的飞书聊天框前，如果 ta 的头像是本人，ta 在个人签名里放了个人说明书，那么读完后，作为 i 人的我会更容易给 ta 发出第一句话，然后和 ta 达成更加顺畅的沟通关系。
                  </p>
                  <p>
                    因此，希望我的个人说明书也能帮助你更好地了解我，以及更顺畅地和我沟通，感恩～
                  </p>
                </div>
              </blockquote>
            </div>
            
            {/* 右侧：互动区域 */}
            <div className="col-span-5">
              <InteractiveSection />
            </div>
          </div>

          {/* 移动端：垂直布局 */}
          <div className="block md:hidden">
            {/* 引用格式的说明文本 */}
            <div className="mb-16">
              <blockquote className="text-gray-600 text-base sm:text-lg leading-relaxed">
                <div className="font-bold mb-4 text-xl text-black">写在最后：为什么要写这份个人说明书？</div>
                <div className="space-y-4">
                  <p>
                    在前司，常常看到各种各样有趣的个人说明书，但是自己一直没有写。一方面是 i 人，另一方面也怕自己不够有趣写不出什么内容。直到离职的时候，写了一份离职说明书，除了交接内容外，也简单写了一下个人的一些信息和爱好，却没想到也收到了大家热烈的回应。
                  </p>
                  <p>
                    以前打开一个陌生人的飞书聊天框前，如果 ta 的头像是本人，ta 在个人签名里放了个人说明书，那么读完后，作为 i 人的我会更容易给 ta 发出第一句话，然后和 ta 达成更加顺畅的沟通关系。
                  </p>
                  <p>
                    因此，希望我的个人说明书也能帮助你更好地了解我，以及更顺畅地和我沟通，感恩～
                  </p>
                </div>
              </blockquote>
            </div>
            
            {/* 互动区域 */}
            <InteractiveSection />
          </div>
        </div>

        <div className="px-4 md:px-8">
          {/* 桌面端：保持原有12列网格布局 */}
          <div className="hidden md:grid grid-cols-12 gap-0">
            <div className="col-span-6">
              <h3 className="text-3xl font-bold mb-4">Contact Me</h3>
              <div className="space-y-2 text-lg">
                <div>邮箱：huangehow@yeah.net</div>
                <div>电话：17688230414</div>
                <div>微信：huangehow</div>
              </div>
            </div>
            <div className="col-span-6 flex justify-end items-end">
              <div
                className="text-right"
                onClick={handleHiddenEntrance}>

                <div className="text-4xl font-black mb-2">Ehow's</div>
                <div className="text-4xl font-black mb-2">User Manual</div>
                <div className="text-lg">Latest Update: {lastUpdateDate}</div>
              </div>
            </div>
          </div>

          {/* 移动端：单列垂直布局 */}
          <div className="block md:hidden">
            <div className="space-y-8">
              {/* 联系信息 */}
              <div>
                <h3 className="text-2xl font-bold mb-4 border-b-2 border-black pb-2">Contact Me</h3>
                <div className="space-y-2 text-base">
                  <div>邮箱：huangehow@yeah.net</div>
                  <div>电话：17688230414</div>
                  <div>微信：huangehow</div>
                </div>
              </div>
              
              {/* 版权信息 */}
              <div
                className="text-center border-t-2 border-black pt-6"
                onClick={handleHiddenEntrance}>

                <div className="text-2xl sm:text-3xl font-black mb-2">Ehow's User Manual</div>
                <div className="text-sm text-gray-600">Latest Update: {lastUpdateDate}</div>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Location Modal */}
      <LocationModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        location={modalLocation} />


      {/* Social Card Modal */}
      <SocialCardModal
        isOpen={isSocialModalOpen}
        onClose={handleCloseSocialModal}
        socialCard={modalSocialCard} />

    </div>);

}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>);

}

export default App;